import { useEffect, useState, useCallback, useMemo } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Store,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  get_vendor_list,
  get_vendor_master_data,
  save_update_vendor,
  server_post_data,
  delete_vendor,
} from "../ServiceConnection/serviceconnection";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";

export default function VendorMaster() {
  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  // Dialog states
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Selected items
  const [editingVendor, setEditingVendor] = useState(null);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    vendorName: "",
    email: "",
    contact: "",
    address: "",
    departments: [],
  });
  const [errors, setErrors] = useState({});

  // Memoized helper function
  const getDepartmentNamesArray = useCallback((departmentString) => {
    if (!departmentString) return [];
    return departmentString
      .split(",")
      .map((dept) => dept.trim())
      .filter((dept) => dept);
  }, []);

  // Filter vendors - memoized
  // Replace the current filteredVendors definition with this:

  const filteredVendors = useMemo(() => {
    if (!vendors || vendors.length === 0) return [];

    const query = searchQuery.toLowerCase().trim();

    // First filter, then sort alphabetically by vendor_name
    return vendors
      .filter(
        (vendor) =>
          vendor.vendor_name.toLowerCase().includes(query) ||
          (vendor.vendor_email || "").toLowerCase().includes(query) ||
          (vendor.vendor_contact || "").includes(query)
      )
      .sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
  }, [vendors, searchQuery]);

  // API Functions
  const master_data_get = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const response = await server_post_data(get_vendor_list, fd);
      if (!response.data.error) {
        setVendors(response.data.message || []);
      }
    } catch (error) {
      console.error("Network error:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  const master_data_get_departments = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const response = await server_post_data(get_vendor_master_data, fd);
      if (!response.data.error) {
        const apiData = response.data.message || {};
        setDepartments(apiData.departments || []);
      }
    } catch (error) {
      console.error("Network error:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  // Form functions
  const resetForm = useCallback(() => {
    setFormData({
      vendorName: "",
      email: "",
      contact: "",
      address: "",
      departments: [],
    });
    setErrors({});
    setEditingVendor(null);
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleDepartmentToggle = useCallback((deptId) => {
    setFormData((prev) => {
      const isSelected = prev.departments.includes(deptId);
      return {
        ...prev,
        departments: isSelected
          ? prev.departments.filter((id) => id !== deptId)
          : [...prev.departments, deptId],
      };
    });
    setErrors((prev) => {
      if (prev.departments) {
        const { departments: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.vendorName.trim()) {
      newErrors.vendorName = "Vendor name is required";
    }

    // Email is optional, but if filled → must be valid
    if (
      formData.email?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      newErrors.email = "Invalid email format";
    }

    // Keep contact strict (10 digits)
    if (!formData.contact || !/^\d{10}$/.test(formData.contact)) {
      newErrors.contact = "Contact number must be exactly 10 digits";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (formData.departments.length === 0) {
      newErrors.departments = "Select at least one department";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Dialog handlers
  const handleCreateClick = useCallback(() => {
    resetForm();
    setIsFormDialogOpen(true);
    master_data_get_departments();
  }, [resetForm, master_data_get_departments]);

  const handleEditClick = useCallback(
    (vendor) => {
      master_data_get_departments();
      setEditingVendor(vendor);

      const departmentIdsArray = vendor.department_ids
        ? vendor.department_ids.split(",").map((id) => parseInt(id.trim()))
        : [];

      setFormData({
        vendorName: vendor.vendor_name,
        email: vendor.vendor_email,
        contact: vendor.vendor_contact,
        address: vendor.vendor_address || "",
        departments: departmentIdsArray,
      });
      setErrors({});
      setIsFormDialogOpen(true);
    },
    [master_data_get_departments]
  );

  const handleViewClick = useCallback((vendor) => {
    setViewingVendor(vendor);
    setIsViewDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((vendor) => {
    setVendorToDelete(vendor);
    setIsDeleteDialogOpen(true);
  }, []);

  // Save/Update handler
  const handleSaveChanges = useCallback(async () => {
    if (!validateForm()) {
      handleError("Please fill in all required fields correctly");

      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("vendor_name", formData.vendorName);
    fd.append("vendor_contact", formData.contact);
    fd.append("vendor_email", formData.email);
    fd.append("vendor_address", formData.address);

    const departmentIdsString = Array.isArray(formData.departments)
      ? formData.departments.join(",")
      : formData.departments;

    fd.append("department_ids", departmentIdsString);

    if (editingVendor) {
      fd.append("vendor_id", editingVendor.vendor_id);
    }

    try {
      const response = await server_post_data(save_update_vendor, fd);

      if (response.data.error) {
        handleError(response.data.message);
      } else {
        handleSuccess(
          "Success",
          editingVendor
            ? "Vendor updated successfully"
            : "Vendor created successfully"
        );

        master_data_get();
        setIsFormDialogOpen(false);
        resetForm();
      }
    } catch (error) {
      handleError("network");
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [validateForm, formData, editingVendor, master_data_get, resetForm]);

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("vendor_id", vendorToDelete.vendor_id);

    try {
      const response = await server_post_data(delete_vendor, fd);

      if (response.data.error) {
        handleError(response.data.message || "Failed to delete vendor");
      } else {
        handleError(
          `${vendorToDelete.vendor_name} has been deleted successfully`
        );

        master_data_get();
      }
    } catch (error) {
      console.error("Delete error:", error);
      handleError("network");
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setVendorToDelete(null);
    }
  }, [vendorToDelete, master_data_get]);

  // Initial load
  useEffect(() => {
    master_data_get();
  }, [master_data_get]);

  return (
    <MainLayout>
      <Header
        title="Vendor Management"
        subtitle="Manage your vendors and suppliers"
      />

      <div className="p-6 space-y-6">
        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={handleCreateClick}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            New Vendor
          </Button>
        </div>

        {/* Vendors Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">
                  Vendor Name
                </TableHead>
                <TableHead className="text-muted-foreground">Email</TableHead>
                <TableHead className="text-muted-foreground">Contact</TableHead>
                <TableHead className="text-muted-foreground">
                  Departments
                </TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Store className="w-8 h-8" />
                      <p>No vendors found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => {
                  const deptNames = getDepartmentNamesArray(
                    vendor.department_names
                  );
                  const displayDepts = deptNames.slice(0, 2);
                  const remainingCount = deptNames.length - 2;

                  return (
                    <TableRow
                      key={vendor.vendor_id}
                      className="border-border hover:bg-muted/30"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                            <Store className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <p className="font-medium text-foreground">
                            {vendor.vendor_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                          <Mail className="w-3.5 h-3.5" />

                          {vendor.vendor_email &&
                          vendor.vendor_email.trim() !== ""
                            ? vendor.vendor_email.trim()
                            : "Not Provided"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                          <Phone className="w-3.5 h-3.5" />
                          {vendor.vendor_contact}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {displayDepts.map((dept, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="text-xs font-normal"
                            >
                              {dept}
                            </Badge>
                          ))}
                          {remainingCount > 0 && (
                            <Badge
                              variant="secondary"
                              className="text-xs font-normal"
                            >
                              +{remainingCount}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewClick(vendor)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(vendor)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(vendor)}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create/Edit Form Dialog */}
        <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingVendor ? "Edit Vendor" : "Add New Vendor"}
              </DialogTitle>
              <DialogDescription>
                {editingVendor
                  ? "Update vendor details and department assignments"
                  : "Add a new vendor to your system"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Vendor Name */}
              <div className="space-y-2">
                <Label htmlFor="vendorName">
                  Vendor Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="vendorName"
                  placeholder="e.g., Metro Cash & Carry"
                  value={formData.vendorName}
                  onChange={(e) =>
                    handleFieldChange("vendorName", e.target.value)
                  }
                  className={errors.vendorName ? "border-destructive" : ""}
                />
                {errors.vendorName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.vendorName}
                  </p>
                )}
              </div>

              {/* Email and Contact in Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@vendor.com"
                    value={formData.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact">
                    Contact Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.contact}
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value.replace(/\D/g, "");
                      handleFieldChange("contact", onlyNumbers);
                    }}
                    className={errors.contact ? "border-destructive" : ""}
                  />
                  {errors.contact && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.contact}
                    </p>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">
                  Address <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="address"
                  placeholder="Enter complete address with pincode"
                  value={formData.address}
                  onChange={(e) => handleFieldChange("address", e.target.value)}
                  className={cn(
                    "resize-none",
                    errors.address ? "border-destructive" : ""
                  )}
                  rows={3}
                />
                {errors.address && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.address}
                  </p>
                )}
              </div>

              {/* Departments Selection */}
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <Label>
                  Select Departments <span className="text-destructive">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {departments.map((dept) => (
                    <div
                      key={dept.primary_id}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`dept-${dept.primary_id}`}
                        checked={formData.departments.includes(dept.primary_id)}
                        onCheckedChange={() =>
                          handleDepartmentToggle(dept.primary_id)
                        }
                      />
                      <label
                        htmlFor={`dept-${dept.primary_id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {dept.department_name}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.departments && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.departments}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsFormDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin
                  ? "Saving..."
                  : editingVendor
                  ? "Update Vendor"
                  : "Add Vendor"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Vendor Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Vendor Details</DialogTitle>
              <DialogDescription>
                Complete information about this vendor
              </DialogDescription>
            </DialogHeader>

            {viewingVendor && (
              <div className="space-y-6 py-4">
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                    <Store className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">
                      {viewingVendor.vendor_name}
                    </h3>
                    <Badge className="mt-1 bg-success/20 text-success border-success/30 font-medium border">
                      Active Vendor
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">
                    Contact Information
                  </h4>
                  <div className="grid gap-3">
                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <Mail className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm font-medium text-foreground">
                          {viewingVendor.vendor_email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <Phone className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Contact Number
                        </p>
                        <p className="text-sm font-medium text-foreground">
                          {viewingVendor.vendor_contact}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <MapPin className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Address</p>
                        <p className="text-sm font-medium text-foreground">
                          {viewingVendor.vendor_address || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">
                    Assigned Departments
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {getDepartmentNamesArray(
                      viewingVendor.department_names
                    ).map((dept, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-sm font-normal px-3 py-1"
                      >
                        {dept}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Created On</span>
                    <span className="font-medium text-foreground">
                      {new Date(viewingVendor.entry_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Vendor ID</span>
                    <span className="font-medium text-foreground">
                      #{viewingVendor.vendor_id}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsViewDialogOpen(false);
                  handleEditClick(viewingVendor);
                }}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Vendor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "
                <strong>{vendorToDelete?.vendor_name}</strong>"? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={showLoaderAdmin}
                className="bg-destructive hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
